import Button from "@/components/bricks/button/Button";
import { useContext, useEffect, useState } from "react";
import { FormikDatePicker } from "../../formcontrols/DatePicker";
import { TextInputFormik } from "../../formcontrols/InputItem";
import { FormikTextArea } from "../../formcontrols/TextArea";
import FirmaIcon from "../../icons/FirmaIcon";
import { ModalContext } from "../../Modal/Modal";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "../../select/Select";
import {
  TicketHeading,
  TicketRoot,
  TicketSub,
  TicketSubItem,
} from "../../ticket/Ticket";
import { WizardReceipt } from "../common/wizardReceipt";
import { useBeskikketAdvokat } from "./useBeskikketAdvokat";
import { BooleanCheckboxFormControl } from "../../formcontrols/InputCheckbox";
import InputRadio from "../../formcontrols/InputRadio";
import ValidationErrorMessage from "../../formcontrols/ValidationErrorMessage";
import { FirmaRelationKodeDict } from "../OpretPerson/useOpretPerson";
import AutoCompleteFirma from "../../autocomplete/AutoCompleteFirma";
import { AutoCompleteFormSelectedItem } from "../../autocomplete/AutoCompleteListItem";
import { StickyFooter } from "../../stickyFooter/modalStickyFooter";
import UdenlandskAdvokatsamfundFormControl from "../../formcontrols/UdenlandskAdvokatsamfundFormControl";
import { PersonDto } from "../../../interfaces/schema";
import { AlertMessage } from "../../AlertMessage";
import usePersonCprNumber from "../../../hooks/usePersonCprNumber";
import { cprAdgangInfo, canEditFoedselsDato } from "../../../util/cprAccess";
import Spinner from "../../spinner/Spinner";
import { differenceInCalendarDays } from "date-fns";
import { dateIsWithinInterval } from "../../../util/date";

interface IBeskikAdvokatWizardProps {
  person: PersonDto;
  invalidateData: () => void;
}
interface IBeskikAdvokatWizardWithCprProps extends IBeskikAdvokatWizardProps {
  cpr: string;
  invalidateCpr: InvalidateDataFunction<any>;
  canReadCpr: boolean;
}

export default function BeskikAdvokatWizardCprWrapper(
  props: IBeskikAdvokatWizardProps,
) {
  const { canReadCpr, cpr, cprLoading, invalidateCpr } = usePersonCprNumber(
    props.person.id,
    true,
  );
  if (cprLoading) {
    return (
      <div
        className="c-modal-container d-flex flex-column justify-content-center align-items-center"
        style={{ height: "400px", maxHeight: "calc(100vh - 200px)" }}
      >
        <Spinner size={48} />
      </div>
    );
  }
  // ADV-349: uden CprRead hentes CPR-nummeret ikke; wizarden kører uden løbenummer.
  if (canReadCpr && cpr === undefined) return <p>CPR-kald fejlet</p>;
  return (
    <BeskikketAdvokatWizard
      {...props}
      cpr={cpr ?? ""}
      invalidateCpr={invalidateCpr}
      canReadCpr={canReadCpr}
    />
  );
}

function BeskikketAdvokatWizard({
  cpr,
  invalidateCpr,
  invalidateData,
  person,
  canReadCpr,
}: IBeskikAdvokatWizardWithCprProps) {
  const { setModalOpen } = useContext(ModalContext);
  const { formik, beskikAdvokatQuery, patchError, resetPersonQuery } =
    useBeskikketAdvokat(person, cpr, canReadCpr);

  const { data, error, isPending, reset: resetQuery } = beskikAdvokatQuery;
  const {
    values,
    setValues,
    errors,
    setTouched,
    setFieldValue,
    handleSubmit,
    touched,
    isSubmitting,
  } = formik;
  const {
    erUdenlandskAdvokat,
    firmaRelation,
    nytFirma,
    sammeFirma,
    udenlandskAdvokatSamfund,
    beskikkelseFraDato,
  } = values;

  const [nytFirmaShouldCaptureFocus, setNytFirmaShouldCaptureFocus] =
    useState(false);
  if (patchError) {
    return <ReceiptError error={patchError} resetQuery={resetPersonQuery} />;
  }
  if (error) return <ReceiptError error={error} resetQuery={resetQuery} />;
  if (data) {
    return (
      <Receipt invalidateData={invalidateData} invalidateCpr={invalidateCpr} />
    );
  }
  const compareDate = beskikkelseFraDato ? beskikkelseFraDato : new Date();
  const aktiveFirmaer = person.personFirma?.filter(
    (element) =>
      differenceInCalendarDays(
        new Date(element?.periode.tilDatoInklusiv),
        compareDate,
      ) >= 0,
  );
  return (
    <div className="c-modal-container">
      <form onSubmit={handleSubmit} autoComplete="off">
        <fieldset disabled={isPending}>
          <div className="c-item-list c-modal-container-inner">
            <h3 className="mb-n2 flex-100">Fødselsdato</h3>
            <div className="flex-100 flex-row align-items-end gap-2">
              <div className="flex-grow-1 position-relative">
                <FormikDatePicker
                  formik={formik}
                  disabled={
                    !canEditFoedselsDato(canReadCpr, {
                      harCprNummer: !!person.harCprNummer,
                      foedselsDato: person.foedselsDato,
                    })
                  }
                  config={{
                    component: "datepicker",
                    id: "foedselsDato",
                    label: canReadCpr
                      ? "Fødselsdato* / CPR-nr."
                      : "Fødselsdato*",
                  }}
                />
                {cprAdgangInfo(canReadCpr, {
                  harCprNummer: !!person.harCprNummer,
                  foedselsDato: person.foedselsDato,
                }) && (
                  <AlertMessage type="info" className="mt-2">
                    {cprAdgangInfo(canReadCpr, {
                      harCprNummer: !!person.harCprNummer,
                      foedselsDato: person.foedselsDato,
                    })}
                  </AlertMessage>
                )}
              </div>
              {canReadCpr && (
                <div className="d-flex align-items-center gap-1">
                  <span>-</span>
                  <TextInputFormik
                    config={{
                      component: "textinput",
                      id: "cprNr",
                      label: " ",
                      placeholder: "0000",
                      initialValue: "",
                    }}
                    formik={formik}
                  />
                </div>
              )}
            </div>
            <h3 className="mb-n2 mt-2 flex-100">EU-advokat</h3>
            <div className="flex-100 d-block">
              <BooleanCheckboxFormControl
                formik={formik}
                config={{
                  component: "checkbox",
                  id: "erUdenlandskAdvokat",
                  label: "",
                  inputLabel: "Personen er EU-advokat",
                }}
              />
            </div>
            {erUdenlandskAdvokat && (
              <div className="flex-100 d-block">
                <label className="form-label">Udenlandsk advokatsamfund</label>
                <UdenlandskAdvokatsamfundFormControl
                  classNameContainer={`${
                    touched.udenlandskAdvokatSamfund &&
                    errors.udenlandskAdvokatSamfund
                      ? "is-invalid"
                      : ""
                  }`}
                  onChange={(val) => {
                    setTouched({
                      ...touched,
                      udenlandskAdvokatSamfund: true,
                    });
                    setValues(
                      { ...values, udenlandskAdvokatSamfund: val },
                      true,
                    );
                  }}
                  value={udenlandskAdvokatSamfund}
                />
                <ValidationErrorMessage
                  touched={touched.udenlandskAdvokatSamfund}
                  errorMessage={errors.udenlandskAdvokatSamfund}
                />
              </div>
            )}
            <h3 className="mb-n2 mt-2">Beskikkelse</h3>
            <div className="flex-100">
              <FormikDatePicker
                formik={formik}
                config={{
                  component: "datepicker",
                  id: "beskikkelseFraDato",
                  label: "Startdato",
                  isRequired: true,
                  initialValue: "",
                }}
              />
            </div>
            <div className="flex-100">
              <FormikTextArea
                formik={formik}
                config={{
                  component: "textarea",
                  id: "aarsag",
                  label: "Årsag",
                  initialValue: "",
                  placeholder: "Angiv årsag",
                }}
              />
            </div>
            <h3 className="mb-n2 mt-2 flex-100">Firmarelation</h3>
            {showCheckPersonFirmas(person, compareDate)}

            {aktiveFirmaer &&
              aktiveFirmaer.length > 0 &&
              showCheckPersonFirmas(person, compareDate) === null && (
                <>
                  <div className="flex-100">
                    <p className="form-label">
                      Personen er allerede tilknyttet firmaet:
                    </p>
                    <TicketRoot
                      className="bg-light w-100"
                      iconLeft={<FirmaIcon height={22} />}
                    >
                      <TicketHeading>
                        <>
                          {aktiveFirmaer[0].firma.navn1
                            ? aktiveFirmaer[0].firma.navn1
                            : ""}
                        </>
                      </TicketHeading>
                      <TicketSub>
                        <TicketSubItem>
                          Firmanr. {aktiveFirmaer[0]?.firma.firmaNr} |
                        </TicketSubItem>
                        <TicketSubItem>
                          Postnr.{" "}
                          {
                            aktiveFirmaer[0]?.firma.beliggenhedsadresse
                              .postnummer
                          }{" "}
                          |
                        </TicketSubItem>
                        <TicketSubItem>
                          CVR-nr. {aktiveFirmaer[0].firma.cvrNr}
                        </TicketSubItem>
                      </TicketSub>
                    </TicketRoot>
                  </div>
                  <div className="flex-100 d-block">
                    <p>Fortsætter i samme firma?</p>
                    <div className="d-flex">
                      <InputRadio
                        label="Ja"
                        className=""
                        id="sammeFirmaRadio1"
                        name="sammeFirmaRadio"
                        checked={sammeFirma === 1 ? true : false}
                        onChange={(e) => {
                          if (e.currentTarget.checked) {
                            setValues({ ...values, sammeFirma: 1 }, true);
                          }
                        }}
                      />
                      <InputRadio
                        label="Ja, men personen skal også ansættes i et firma mere"
                        className=""
                        id="sammeFirmaRadio2"
                        name="sammeFirmaRadio"
                        checked={sammeFirma === 2 ? true : false}
                        onChange={(e) => {
                          if (e.currentTarget.checked) {
                            setValues({ ...values, sammeFirma: 2 }, true);
                          }
                        }}
                      />
                      <InputRadio
                        label="Nej"
                        className=""
                        id="sammeFirmaRadio3"
                        name="sammeFirmaRadio"
                        checked={sammeFirma === 3 ? true : false}
                        onChange={(e) => {
                          if (e.currentTarget.checked) {
                            setValues({ ...values, sammeFirma: 3 }, true);
                          }
                        }}
                      />
                    </div>
                  </div>
                </>
              )}
            {(aktiveFirmaer && aktiveFirmaer.length === 0) ||
            sammeFirma === 2 ||
            sammeFirma === 3 ? (
              <>
                <div className="flex-100">
                  <label htmlFor="" className="form-label">
                    Firma*
                  </label>
                  {!nytFirma && (
                    <AutoCompleteFirma
                      className="w-100"
                      isInvalidClass={
                        touched.nytFirma && errors.nytFirma ? "is-invalid" : ""
                      }
                      includeFirstItem={false}
                      onSelectedItemChange={(item) => {
                        setNytFirmaShouldCaptureFocus(true);
                        setTouched({ ...touched, nytFirma: true });
                        setFieldValue("nytFirma", item.selectedItem);
                      }}
                      onInputBlur={() => {
                        setTouched({ ...touched, nytFirma: true });
                      }}
                      focusInputOnMount={nytFirmaShouldCaptureFocus}
                    />
                  )}
                  {nytFirma && (
                    <AutoCompleteFormSelectedItem
                      title={"Fjern firma"}
                      item={nytFirma}
                      onClick={() => {
                        setNytFirmaShouldCaptureFocus(true);
                        setFieldValue("nytFirma", undefined);
                      }}
                      focusOnMount={nytFirmaShouldCaptureFocus}
                    />
                  )}
                  <ValidationErrorMessage
                    touched={touched.nytFirma}
                    errorMessage={errors.nytFirma}
                  />
                </div>
                <div className="flex-100">
                  <TextInputFormik
                    formik={formik}
                    config={{
                      component: "textinput",
                      id: "emailArbejde",
                      label: "Email, arbejde",
                      placeholder: "Indtast email",
                      initialValue: "",
                    }}
                  />
                </div>
                <div>
                  <FormikDatePicker
                    formik={formik}
                    config={{
                      component: "datepicker",
                      id: "nytFirmaFraDato",
                      label: "Startdato",
                      isRequired: true,
                      initialValue: "",
                    }}
                  />
                </div>
                <div>
                  <Select
                    value={firmaRelation.kode.toString()}
                    onValueChange={(value) => {
                      switch (value) {
                        case FirmaRelationKodeDict.ansat.kode.toString(): {
                          setFieldValue(
                            "firmaRelation",
                            FirmaRelationKodeDict.ansat,
                            true,
                          );
                          break;
                        }
                        case FirmaRelationKodeDict.partner.kode.toString(): {
                          setFieldValue(
                            "firmaRelation",
                            FirmaRelationKodeDict.partner,
                            true,
                          );
                          break;
                        }
                        case FirmaRelationKodeDict.ejer.kode.toString(): {
                          setFieldValue(
                            "firmaRelation",
                            FirmaRelationKodeDict.ejer,
                            true,
                          );
                          break;
                        }
                      }
                    }}
                  >
                    <label className="form-label">{"Firmarelation"}</label>
                    <SelectTrigger />
                    <SelectContent>
                      <SelectItem
                        value={FirmaRelationKodeDict.ansat.kode.toString()}
                      >
                        {FirmaRelationKodeDict.ansat.beskrivelse}
                      </SelectItem>
                      <SelectItem
                        value={FirmaRelationKodeDict.partner.kode.toString()}
                      >
                        {FirmaRelationKodeDict.partner.beskrivelse}
                      </SelectItem>
                      <SelectItem
                        value={FirmaRelationKodeDict.ejer.kode.toString()}
                      >
                        {FirmaRelationKodeDict.ejer.beskrivelse}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            ) : null}
          </div>
          <StickyFooter>
            <Button
              color="secondary"
              onClick={() => {
                setModalOpen(false);
              }}
            >
              Annuller
            </Button>
            <Button
              type="submit"
              color="primary"
              disabled={isSubmitting || isPending}
            >
              Beskik advokat
            </Button>
          </StickyFooter>
        </fieldset>
      </form>
    </div>
  );
}

const ReceiptError = (props: { error: any; resetQuery: any }) => {
  const { error, resetQuery } = props;
  const errorMsg = (error as any).data?.Message
    ? (error as any).data.Message
    : ((error as any).data ?? null);
  return (
    <div className="c-modal-container">
      <WizardReceipt heading="Noget gik galt" icon="warning">
        {errorMsg ? <p className="text-muted text-center">{errorMsg}</p> : null}
      </WizardReceipt>
      <div className="flex-100 d-flex justify-content-center mt-3">
        <Button
          color="secondary"
          onClick={() => {
            resetQuery();
          }}
        >
          Tilbage
        </Button>
      </div>
    </div>
  );
};
const Receipt = (props: {
  invalidateData: () => void;
  invalidateCpr: () => void;
}) => {
  const { invalidateData, invalidateCpr } = props;
  useEffect(() => {
    invalidateCpr();
    invalidateData();
  }, [invalidateData, invalidateCpr]);
  const { setModalOpen } = useContext(ModalContext);
  return (
    <div className="c-modal-container">
      <WizardReceipt heading="Beskikkelse er gennemført" icon="success">
        <p className="text-muted text-center"></p>
      </WizardReceipt>
      <div className="flex-100 d-flex justify-content-center mt-3">
        <Button
          color="secondary"
          onClick={() => {
            setModalOpen(false);
          }}
        >
          Luk
        </Button>
      </div>
    </div>
  );
};

function showCheckPersonFirmas(person: PersonDto, compareDate: Date) {
  const aktiveFirmaer = person.personFirma?.filter((element) =>
    dateIsWithinInterval(
      compareDate,
      new Date(element.periode.fraDato),
      new Date(element?.periode.tilDatoInklusiv),
    ),
  );
  const futureFirmaer = person.personFirma?.filter(
    (element) => new Date(element?.periode.fraDato) > compareDate,
  );
  if (
    (aktiveFirmaer && aktiveFirmaer.length > 1) ||
    (futureFirmaer && futureFirmaer.length > 0)
  ) {
    return (
      <AlertMessage
        warningIcon
        heading="Tjek at firmarelationer på personens stamoplysninger er korrekte
      efter de er beskikket"
      >
        <ul className="fw-medium">
          {aktiveFirmaer && aktiveFirmaer.length > 1 && (
            <li>
              {person.fornavne} {person.efternavn} har mere end een eksisterende
              firmarelation.
            </li>
          )}

          {futureFirmaer && futureFirmaer.length > 0 && (
            <li className="fw-medium">
              {person.fornavne} {person.efternavn} har mindst een fremtidig
              firmarelation i forhold til beskikkelsesdato.
            </li>
          )}
        </ul>
      </AlertMessage>
    );
  }
  return null;
}
